import { useEffect, useReducer, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router";
import { CheckCircle2, Puzzle, ShieldAlert } from "lucide-react";
import { approveExtensionLink, deliverExtensionLink, fetchExtensionLink } from "../../api/extensionLinks.js";
import type { LinkApproval, LinkInfo } from "../../api/extensionLinks.js";
import { ApiClientError, apiBaseUrl } from "../../api/http.js";
import { paths } from "../../app/paths.js";
import { useSession } from "../../app/session.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import type { CryptoClient } from "../../cryptoClient.js";
import {
  LINK_EXPIRED_MESSAGE,
  LINK_NO_STORAGE_MESSAGE,
  approveFailure,
  dropLinkRequest,
  formatCountdown,
  linkIdB64,
  linkReducer,
  linkServerUrl,
  linkStorage,
  normalizeLinkCode,
  readLinkRequest,
  sameLinkKey
} from "../../lib/extensionLink.js";
import type { LinkPhase, LinkRequest } from "../../lib/extensionLink.js";
import { Alert, Button, Card, EmptyState, Page, PageHeader, Skeleton, buttonClass } from "../../ui/primitives.js";
import { Checkbox, Field, OtpInput } from "../../ui/forms.js";

function loadFailure(err: unknown): string {
  if (err instanceof ApiClientError) {
    return err.status === 404 ? LINK_EXPIRED_MESSAGE : err.message;
  }
  return "Die Anfrage konnte nicht geladen werden. Prüfe deine Verbindung und öffne den Link aus der Erweiterung erneut.";
}

const DELIVERY_FAILED = "Die Übergabe an die Erweiterung ist fehlgeschlagen. Starte „Mit Passkey anmelden“ in der Erweiterung neu.";

/**
 * Seals and hands over. The approval (with the extension's token) is only passed through here to
 * the crypto worker, which returns nothing but the sealed blob. It is never stored or logged.
 */
async function sealAndDeliver(client: CryptoClient, request: LinkRequest, approval: LinkApproval): Promise<void> {
  const requestIdB64 = linkIdB64(request.id);
  const serverUrl = linkServerUrl(apiBaseUrl(), window.location.origin);
  if (requestIdB64 === undefined || serverUrl === undefined) {
    throw new DeliveryError();
  }
  let sealedB64: string;
  try {
    sealedB64 = await client.sealForExtension({
      requestIdB64,
      publicKeyB64: request.publicKeyB64,
      serverUrl,
      userId: approval.userId,
      email: approval.email,
      token: approval.token,
      expiresAt: approval.expiresAt,
      saltB64: approval.saltB64
    });
  } catch {
    throw new DeliveryError();
  }
  try {
    await deliverExtensionLink(request.id, sealedB64);
  } catch {
    throw new DeliveryError();
  }
}

class DeliveryError extends Error {
  public readonly code = "link-delivery";

  public constructor() {
    super(DELIVERY_FAILED);
    this.name = "DeliveryError";
  }
}

function useNow(active: boolean): number {
  const [now, setNow] = useState((): number => Date.now());
  useEffect((): (() => void) | undefined => {
    if (!active) {
      return undefined;
    }
    const timer = window.setInterval((): void => setNow(Date.now()), 1000);
    return (): void => window.clearInterval(timer);
  }, [active]);
  return now;
}

function initialPhase(request: LinkRequest | undefined): LinkPhase {
  if (request !== undefined) {
    return { kind: "loading" };
  }
  return linkStorage() === undefined ? { kind: "failed", message: LINK_NO_STORAGE_MESSAGE } : { kind: "missing" };
}

export function LinkExtensionPage(): ReactNode {
  useDocumentTitle("Erweiterung verbinden");
  const { state } = useSession();
  const client = state.status === "locked" ? undefined : state.client;
  const [request] = useState((): LinkRequest | undefined => {
    const storage = linkStorage();
    return storage === undefined ? undefined : readLinkRequest(storage, Date.now());
  });
  const [phase, dispatch] = useReducer(linkReducer, request, initialPhase);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const counting = phase.kind === "review" || phase.kind === "approving";
  const now = useNow(counting);

  useEffect((): (() => void) | undefined => {
    if (request === undefined) {
      return undefined;
    }
    let live = true;
    fetchExtensionLink(request.id).then(
      (info: LinkInfo): void => {
        if (!live) {
          return;
        }
        if (!sameLinkKey(request.publicKeyB64, info.publicKeyB64)) {
          dispatch({ type: "rejected", message: "Diese Anfrage passt nicht zum Link aus der Erweiterung. Bestätige nichts und starte die Anmeldung in der Erweiterung neu." });
        } else if (info.status !== "pending") {
          dispatch({ type: "rejected", message: "Diese Anfrage wurde bereits bestätigt oder abgelehnt. Starte die Anmeldung in der Erweiterung neu." });
        } else if (info.expiresAt <= Date.now()) {
          dispatch({ type: "expired" });
        } else {
          dispatch({ type: "loaded", info });
        }
      },
      (err: unknown): void => {
        if (live) {
          dispatch({ type: "rejected", message: loadFailure(err) });
        }
      }
    );
    return (): void => {
      live = false;
    };
  }, [request]);

  const expiresAt = phase.kind === "review" || phase.kind === "approving" ? phase.info.expiresAt : undefined;
  useEffect((): void => {
    if (expiresAt !== undefined && now >= expiresAt) {
      dispatch({ type: "expired" });
    }
  }, [expiresAt, now]);

  // The request is single-use: forget it once it ended either way.
  useEffect((): void => {
    if (phase.kind === "done" || phase.kind === "failed") {
      const storage = linkStorage();
      if (storage !== undefined) {
        dropLinkRequest(storage);
      }
    }
  }, [phase.kind]);

  function cancel(): void {
    const storage = linkStorage();
    if (storage !== undefined) {
      dropLinkRequest(storage);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (phase.kind !== "review" || request === undefined || client === undefined) {
      return;
    }
    const normalized = normalizeLinkCode(code);
    if (normalized === undefined) {
      setCodeError("Gib den sechsstelligen Code aus der Erweiterung ein.");
      return;
    }
    setCodeError(undefined);
    dispatch({ type: "submit" });
    let approved = false;
    try {
      // A missing passkey check comes back as PASSKEY_REAUTH_REQUIRED; the request layer then
      // runs the passkey step-up (user verification) and retries once.
      const approval = await approveExtensionLink(request.id, normalized);
      approved = true;
      dispatch({ type: "approved" });
      await sealAndDeliver(client, request, approval);
      dispatch({ type: "delivered" });
    } catch (err: unknown) {
      if (approved || err instanceof DeliveryError) {
        dispatch({ type: "rejected", message: DELIVERY_FAILED });
      } else if (err instanceof ApiClientError) {
        dispatch(approveFailure(err.status, err.code, err.message));
      } else {
        dispatch(approveFailure(0, undefined, "Keine Verbindung zum Server. Versuche es erneut."));
      }
      setCode("");
    }
  }

  return (
    <Page width="narrow">
      <PageHeader
        eyebrow="Browser-Erweiterung"
        title="Erweiterung verbinden"
        description="Die Erweiterung meldet sich mit deinem Konto an, ohne dass du dort Master-Passwort und zweiten Faktor eingibst."
      />
      <div className="mt-7 max-w-xl">
        <LinkBody phase={phase} now={now} code={code} codeError={codeError} onCode={setCode} onSubmit={submit} onCancel={cancel} />
      </div>
    </Page>
  );
}

function LinkBody(props: {
  readonly phase: LinkPhase;
  readonly now: number;
  readonly code: string;
  readonly codeError: string | undefined;
  readonly onCode: (value: string) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  readonly onCancel: () => void;
}): ReactNode {
  const { phase } = props;
  switch (phase.kind) {
    case "missing":
      return (
        <EmptyState icon={Puzzle} title="Keine Anfrage gefunden" action={<Link to={paths.dashboard} className={buttonClass("secondary", "md")}>Zum Dashboard</Link>}>
          Öffne die Erweiterung und klicke dort auf „Mit Passkey anmelden“. Sie öffnet diese Seite mit der passenden Anfrage.
        </EmptyState>
      );
    case "loading":
      return <Skeleton className="h-64" />;
    case "failed":
      return (
        <EmptyState icon={ShieldAlert} title="Nicht verbunden" action={<Link to={paths.dashboard} className={buttonClass("secondary", "md")}>Zum Dashboard</Link>}>
          {phase.message}
        </EmptyState>
      );
    case "done":
      return (
        <EmptyState icon={CheckCircle2} title="Erweiterung verbunden" action={<Link to={paths.dashboard} className={buttonClass("secondary", "md")}>Zum Dashboard</Link>}>
          Die Erweiterung meldet sich jetzt an. Du kannst diesen Tab schließen. Das Gerät erscheint in den Einstellungen unter Sitzungen und lässt sich dort abmelden.
        </EmptyState>
      );
    case "sealing":
      return (
        <Card>
          <p className="p-5 text-sm text-mist" role="status">Übergabe an die Erweiterung …</p>
        </Card>
      );
    case "review":
    case "approving":
      return <ReviewForm {...props} info={phase.info} busy={phase.kind === "approving"} error={phase.kind === "review" ? phase.error : undefined} />;
  }
}

function ReviewForm(props: {
  readonly info: LinkInfo;
  readonly busy: boolean;
  readonly error: string | undefined;
  readonly now: number;
  readonly code: string;
  readonly codeError: string | undefined;
  readonly onCode: (value: string) => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  readonly onCancel: () => void;
}): ReactNode {
  const { info } = props;
  const ageSeconds = Math.max(0, Math.round((props.now - info.createdAt) / 1000));
  // Linking again ends the device's current connection: that needs an explicit yes.
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const blocked = info.existingKeyForDevice && !replaceConfirmed;
  return (
    <Card>
      <form
        className="flex flex-col gap-5 p-5"
        onSubmit={(event): void => {
          if (blocked) {
            event.preventDefault();
            return;
          }
          void props.onSubmit(event);
        }}
      >
        <Alert tone="danger">
          Nur bestätigen, wenn du gerade selbst in der Erweiterung „Mit Passkey anmelden“ geklickt hast.
        </Alert>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-mist-2">Gerät</dt>
          <dd className="text-foam">{info.deviceLabel}</dd>
          <dt className="text-mist-2">Netzwerk</dt>
          <dd className="font-mono text-foam">{info.ipMasked}</dd>
          <dt className="text-mist-2">Angefragt</dt>
          <dd className="text-foam">vor {ageSeconds} s</dd>
          <dt className="text-mist-2">Läuft ab in</dt>
          <dd className="font-mono text-foam" aria-live="off">{formatCountdown(info.expiresAt - props.now)}</dd>
        </dl>

        {!info.sameNetwork ? (
          <Alert tone="danger">
            Die Anfrage kommt aus einem anderen Netzwerk als dieser Browser. Wenn die Erweiterung nicht auf diesem Gerät läuft, brich ab.
          </Alert>
        ) : null}
        {info.existingKeyForDevice ? (
          <div className="flex flex-col gap-3">
            <Alert tone="danger">
              Diese Erweiterung ist bereits verbunden. Wenn du fortfährst, wird die bisherige Verbindung dieses Geräts beendet.
            </Alert>
            <Checkbox id="link-replace" checked={replaceConfirmed} onChange={setReplaceConfirmed}>
              Ich verstehe, dass die bisherige Verbindung dieses Geräts beendet wird.
            </Checkbox>
          </div>
        ) : null}

        <Field id="link-code" label="Code aus der Erweiterung" error={props.codeError} hint="Tippe die sechs Ziffern ab, die die Erweiterung gerade anzeigt.">
          <OtpInput label="Code aus der Erweiterung" value={props.code} onChange={props.onCode} disabled={props.busy} autoFocus invalid={props.codeError !== undefined || props.error !== undefined} />
        </Field>

        {props.error !== undefined ? <Alert tone="danger">{props.error}</Alert> : null}

        <div className="flex flex-wrap gap-2 border-t border-line pt-4">
          <Button type="submit" loading={props.busy} disabled={blocked}>
            Erweiterung auf diesem Gerät verbinden
          </Button>
          <Link to={paths.dashboard} className={buttonClass("ghost", "md")} onClick={props.onCancel}>
            Abbrechen
          </Link>
        </div>
        <p className="text-[0.8125rem] leading-relaxed text-mist-2">
          Zum Verbinden bestätigst du gleich mit deinem Passkey. Die Erweiterung bekommt deine Kontoschlüssel verschlüsselt; der Server sieht sie nicht.
        </p>
      </form>
    </Card>
  );
}
